import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { FadeUp } from "../Reveal";
import { profile } from "../../data/content";
import { MagneticButton } from "../MagneticButton";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { playSuccess, playError } from "../../lib/audioUtils";

const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "";

const inputCls =
  "bg-ink-950/60 border-edge-700 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-magenta-500 focus-visible:border-magenta-500/60 rounded-xl h-12";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", company: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell me your name.";
    if (!form.email.trim()) e.email = "Email required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Hmm, that email looks off.";
    if (!form.message.trim()) e.message = "Add a message so I know how I can help.";
    setErrors(e);
    if (Object.keys(e).length > 0) playError();
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    if (!WEB3FORMS_ACCESS_KEY) {
      playError();
      toast.error("Contact form unavailable", { description: "Missing form configuration." });
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append("access_key", WEB3FORMS_ACCESS_KEY);
    formData.append("name", form.name.trim());
    formData.append("email", form.email.trim());
    formData.append("replyto", form.email.trim());
    formData.append("subject", form.subject.trim() || `Message from ${form.name.trim()} via portfolio`);
    formData.append("message", form.message.trim());
    formData.append("from_name", form.name.trim());

    // Honeypot field: if a bot fills this hidden field, silently stop.
    if (form.company) {
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        playSuccess();
        setSent(true);
        toast.success("Message sent", { description: "Thanks, I'll get back to you soon.", duration: 5000 });
        setForm({ name: "", email: "", subject: "", message: "", company: "" });
      } else {
        playError();
        toast.error("Something went wrong", { description: data.message || "Please try again." });
      }
    } catch (err) {
      toast.error("Couldn’t send your message", { description: "Network error  -  please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 sm:py-28 border-t border-edge-650">
      <div className="container-page">
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-5">
            <FadeUp>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-magenta-300">Contact</div>
            </FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="mt-4 font-display tracking-tight text-3xl sm:text-5xl text-white leading-[1.05]">
                Got a project or an idea? <span className="text-magenta-grad">Let's talk.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-5 text-zinc-300 max-w-md">
                Project, role, partnership, or a question that doesn’t fit a form? Send it anyway. I read everything, and respond within a few days.
              </p>
            </FadeUp>

            <FadeUp delay={0.15} className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 w-16">Email</span>
                <a href={`mailto:${profile.email}`} className="hover:text-white transition-colors" data-testid="contact-email-link">{profile.email}</a>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 w-16">Where</span>
                <span>{profile.location}</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 w-16">Resume</span>
                <a href="/resume.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="contact-resume-link">Download PDF</a>
              </div>
            </FadeUp>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <FadeUp>
              <form onSubmit={onSubmit} noValidate data-testid="contact-form" className="rounded-2xl border border-edge-650 bg-graphite-900/50 p-6 sm:p-8">
                <input type="text" name="company" value={form.company} onChange={update("company")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">Name</Label>
                    <Input id="name" name="name" data-testid="contact-name-input" className={`mt-2 ${inputCls}`} value={form.name} onChange={update("name")} autoComplete="name" placeholder="Your name" aria-invalid={!!errors.name} />
                    {errors.name && <p className="mt-2 text-xs text-red-400">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">Email</Label>
                    <Input id="email" name="email" type="email" data-testid="contact-email-input" className={`mt-2 ${inputCls}`} value={form.email} onChange={update("email")} autoComplete="email" placeholder="you@domain.com" aria-invalid={!!errors.email} />
                    {errors.email && <p className="mt-2 text-xs text-red-400">{errors.email}</p>}
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="subject" className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">Subject (optional)</Label>
                  <Input id="subject" name="subject" data-testid="contact-subject-input" className={`mt-2 ${inputCls}`} value={form.subject} onChange={update("subject")} placeholder="Project, partnership, or just hi" />
                </div>

                <div className="mt-4">
                  <Label htmlFor="message" className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">Message</Label>
                  <Textarea id="message" name="message" rows={6} data-testid="contact-message-textarea" className={`mt-2 ${inputCls.replace('h-12', '')} min-h-[140px]`} value={form.message} onChange={update("message")} placeholder="Tell me about the problem you’re solving." aria-invalid={!!errors.message} />
                  {errors.message && <p className="mt-2 text-xs text-red-400">{errors.message}</p>}
                </div>

                <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-xs text-zinc-500">By submitting, you agree I can reply via email. No newsletter, no spam.</p>
                  <span data-magnetic-wrap="true">
                    <MagneticButton
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      strength={0.18}
                      radius={70}
                      className="rounded-xl bg-magenta-500 text-ink-950 px-5 py-3 text-[15px] font-semibold hover:bg-magenta-300 glow-magenta disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <span className="inline-flex items-center"><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending</span>
                      ) : sent ? (
                        <span className="inline-flex items-center"><CheckCircle2 className="w-4 h-4 mr-2" />Sent  -  send another?</span>
                      ) : (
                        <span className="inline-flex items-center">Send Message<Send className="w-4 h-4 ml-2" /></span>
                      )}
                    </MagneticButton>
                  </span>
                </div>
              </form>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
